"use client";

import { useState, useCallback } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { Textarea } from "@/app/components/ui/textarea";
import { Badge } from "@/app/components/ui/badge";
import { Progress } from "@/app/components/ui/progress";
import { Alert, AlertDescription } from "@/app/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/app/components/ui/select";
import { Smile, Frown, Meh, AlertTriangle, Brain, FileText, BarChart3, RefreshCw, Info, Languages } from "lucide-react";

interface SentimentResult {
  sentiment: 'positive' | 'negative' | 'neutral';
  confidence: number;
  scores: {
    positive: number;
    negative: number;
    neutral: number;
  };
  entities: Array<{
    entity: string;
    sentiment: 'positive' | 'negative' | 'neutral';
    confidence: number;
  }>;
  provider: string;
}

interface SentimentHistory {
  id: string;
  text: string;
  sentiment: 'positive' | 'negative' | 'neutral';
  confidence: number;
  timestamp: Date;
}

export default function SentimentAnalyzerPage() {
  const [text, setText] = useState("");
  const [result, setResult] = useState<SentimentResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [history, setHistory] = useState<SentimentHistory[]>([]);
  const [activeTab, setActiveTab] = useState("analyzer");
  const [language, setLanguage] = useState("en");
  const [providers, setProviders] = useState("google");

  const analyzeSentiment = trpc.entitySentiment.analyze.useMutation();

  const analyzeSentimentHandler = useCallback(async () => {
    if (!text.trim()) {
      setError("Please enter some text to analyze");
      return;
    }

    setIsLoading(true);
    setError("");
    setResult(null);

    try {
      console.log("Sending entity sentiment analysis request:", {
        textLength: text.length,
        language,
        providers
      });
      
      const res = await analyzeSentiment.mutateAsync({
        text: text,
        language: language,
        providers: providers,
      });
      
      console.log("Entity sentiment analysis response:", res);
      
      if (res.raw) {
        // Process the first provider's results
        // Eden AI returns the provider data directly in the response
        const firstProvider = Object.keys(res.raw)[0];
        const providerData = res.raw[firstProvider];
        
        console.log("Full provider data:", providerData);
        
        if (providerData && providerData.items) {
          console.log("Provider data items:", providerData.items);
          console.log("First item structure:", providerData.items[0]);
          
          const entities = providerData.items.map((item: any) => {
            console.log("Processing entity item:", item);
            
            // Map different possible sentiment values to our standard format
            let sentiment = 'neutral';
            if (item.sentiment) {
              const sentimentValue = item.sentiment.toLowerCase();
              if (sentimentValue.includes('positive') || sentimentValue === 'pos') {
                sentiment = 'positive';
              } else if (sentimentValue.includes('negative') || sentimentValue === 'neg') {
                sentiment = 'negative';
              } else {
                sentiment = 'neutral';
              }
            }
            
            return {
              entity: item.entity || item.name || item.text || 'Unknown',
              sentiment: sentiment,
              confidence: item.confidence || item.score || 0.5
            };
          });
          
          console.log("Processed entities:", entities);
          
          // Calculate overall sentiment from entities
          const positiveEntities = entities.filter((e: any) => e.sentiment === 'positive');
          const negativeEntities = entities.filter((e: any) => e.sentiment === 'negative');
          const neutralEntities = entities.filter((e: any) => e.sentiment === 'neutral');
          
          const totalEntities = entities.length;
          let positiveScore = totalEntities > 0 ? positiveEntities.length / totalEntities : 0.33;
          let negativeScore = totalEntities > 0 ? negativeEntities.length / totalEntities : 0.33;
          let neutralScore = totalEntities > 0 ? neutralEntities.length / totalEntities : 0.34;
          
          console.log("Sentiment calculation:", {
            totalEntities,
            positiveEntities: positiveEntities.length,
            negativeEntities: negativeEntities.length,
            neutralEntities: neutralEntities.length,
            positiveScore,
            negativeScore,
            neutralScore
          });
          
          
          // Determine overall sentiment
          let overallSentiment: 'positive' | 'negative' | 'neutral';
          if (positiveScore > negativeScore && positiveScore > neutralScore) {
            overallSentiment = 'positive';
          } else if (negativeScore > positiveScore && negativeScore > neutralScore) {
            overallSentiment = 'negative';
          } else {
            overallSentiment = 'neutral';
          }
          
          // Calculate confidence based on the difference between the highest and second highest scores
          const scores = [positiveScore, negativeScore, neutralScore].sort((a, b) => b - a);
          const maxScore = scores[0];
          const secondMaxScore = scores[1];
          const confidence = Math.max(0.1, Math.min(0.99, maxScore + (maxScore - secondMaxScore) * 0.5));
          
          const analysisResult: SentimentResult = {
            sentiment: overallSentiment,
            confidence,
            scores: {
              positive: positiveScore,
              negative: negativeScore,
              neutral: neutralScore
            },
            entities: entities,
            provider: firstProvider
          };
          
          setResult(analysisResult);
          
          // Add to history
          const historyItem: SentimentHistory = {
            id: Date.now().toString(),
            text: text.substring(0, 100) + (text.length > 100 ? '...' : ''),
            sentiment: overallSentiment,
            confidence,
            timestamp: new Date()
          };
          
          setHistory(prev => [historyItem, ...prev.slice(0, 9)]);
        } else {
          setError("No sentiment analysis results found in the response");
        }
      } else {
        setError("Invalid response format from sentiment analysis API");
      }
    } catch (e) {
      console.error("Sentiment analysis error:", e);
      setError(e instanceof Error ? e.message : "Failed to analyze sentiment. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [text, language, providers, analyzeSentiment]);

  const clearText = () => {
    setText("");
    setResult(null);
    setError("");
  };

  const getSentimentIcon = (sentiment: 'positive' | 'negative' | 'neutral') => {
    switch (sentiment) {
      case 'positive':
        return <Smile className="h-5 w-5 text-green-500" />;
      case 'negative':
        return <Frown className="h-5 w-5 text-red-500" />;
      case 'neutral':
        return <Meh className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getSentimentColor = (sentiment: 'positive' | 'negative' | 'neutral') => {
    switch (sentiment) {
      case 'positive':
        return 'text-green-600 bg-green-50 dark:bg-green-950/20';
      case 'negative':
        return 'text-red-600 bg-red-50 dark:bg-red-950/20';
      case 'neutral':
        return 'text-yellow-600 bg-yellow-50 dark:bg-yellow-950/20';
    }
  };

  const formatConfidence = (confidence: number) => {
    return `${Math.round(confidence * 100)}%`;
  };

  const formatScore = (score: number) => {
    return `${Math.round(score * 100)}%`;
  };

  return (
    <div className="container mx-auto px-4 py-8 animate-fade-in">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4 animate-slide-in-left">Sentiment Analyzer</h1>
          <p className="text-muted-foreground text-lg animate-slide-in-right">
            Detect positive, negative, or neutral tone in your text
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="analyzer">Analyzer</TabsTrigger>
            <TabsTrigger value="details">Analysis Details</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>

          <TabsContent value="analyzer" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Input */}
              <Card className="card-enhanced">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    <span>Text Input</span>
                  </CardTitle>
                  <CardDescription>
                    Enter text to analyze its sentiment
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Textarea
                    placeholder="Enter text here to analyze sentiment..."
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    className="min-h-[200px]"
                  />
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium flex items-center gap-2">
                        <Languages className="h-4 w-4" /> Language
                      </label>
                      <Select value={language} onValueChange={setLanguage}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select language" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="en">English</SelectItem>
                          <SelectItem value="es">Spanish</SelectItem>
                          <SelectItem value="ja">Japanese</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">AI Provider</label>
                      <Select value={providers} onValueChange={setProviders}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select provider" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="google">Google Gemini</SelectItem>
                          <SelectItem value="amazon">Amazon Comprehend</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={analyzeSentimentHandler}
                      className="btn-enhanced hover-lift flex-1"
                      disabled={isLoading || !text.trim()}
                    >
                      {isLoading ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Brain className="h-4 w-4 mr-2" />
                          Analyze Sentiment
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={clearText}
                      variant="outline"
                      className="hover-lift"
                    >
                      Clear
                    </Button>
                  </div>

                  {error && (
                    <Alert className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950">
                      <AlertTriangle className="h-4 w-4 text-red-600" />
                      <AlertDescription className="text-red-800 dark:text-red-200">
                        {error}
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Result */}
              <Card className="card-enhanced">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    <span>Analysis Result</span>
                  </CardTitle>
                  <CardDescription>
                    Sentiment analysis of your text
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {result ? (
                    <div className="space-y-6">
                      {/* Sentiment Display */}
                      <div className="text-center">
                        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${getSentimentColor(result.sentiment)}`}>
                          {getSentimentIcon(result.sentiment)}
                          <span className="font-semibold capitalize">{result.sentiment}</span>
                        </div>
                        <div className="mt-2 text-sm text-muted-foreground">
                          Confidence: {formatConfidence(result.confidence)}
                        </div>
                        <Progress value={result.confidence * 100} className="mt-2 h-2" />
                      </div>

                      {/* Score Breakdown */}
                      <div className="space-y-3">
                        <h4 className="font-medium">Sentiment Scores</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="flex items-center gap-1">
                              <Smile className="h-4 w-4 text-green-500" />
                              Positive
                            </span>
                            <span>{formatScore(result.scores.positive)}</span>
                          </div>
                          <Progress value={result.scores.positive * 100} className="h-2" />
                          
                          <div className="flex justify-between text-sm">
                            <span className="flex items-center gap-1">
                              <Frown className="h-4 w-4 text-red-500" />
                              Negative
                            </span>
                            <span>{formatScore(result.scores.negative)}</span>
                          </div>
                          <Progress value={result.scores.negative * 100} className="h-2" />
                          
                          <div className="flex justify-between text-sm">
                            <span className="flex items-center gap-1">
                              <Meh className="h-4 w-4 text-yellow-500" />
                              Neutral
                            </span>
                            <span>{formatScore(result.scores.neutral)}</span>
                          </div>
                          <Progress value={result.scores.neutral * 100} className="h-2" />
                        </div>
                      </div>

                      {/* Entities */}
                      {result.entities && result.entities.length > 0 && (
                        <div className="space-y-3">
                          <h4 className="font-medium">Detected Entities & Sentiment</h4>
                          <div className="space-y-2">
                            {result.entities.map((entity, index) => (
                              <div key={index} className="flex items-center justify-between p-2 border rounded-lg">
                                <span className="font-medium">{entity.entity}</span>
                                <div className="flex items-center gap-2">
                                  {getSentimentIcon(entity.sentiment)}
                                  <span className="text-sm capitalize">{entity.sentiment}</span>
                                  <Badge variant="outline" className="text-xs">
                                    {Math.round(entity.confidence * 100)}%
                                  </Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Powered by {result.provider} • {result.entities.length} entities detected
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center text-muted-foreground py-8">
                      <Brain className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Enter text and click "Analyze Sentiment" to see results</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="details" className="space-y-6">
            <Card className="card-enhanced">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Info className="h-5 w-5" />
                  <span>How Sentiment Analysis Works</span>
                </CardTitle>
                <CardDescription>
                  Understanding the sentiment analysis process
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="font-semibold">AI-Powered Analysis</h3>
                    <p className="text-sm text-muted-foreground">
                      Our sentiment analyzer uses advanced AI models from Google Gemini and Amazon Comprehend to perform entity-level sentiment analysis. 
                      It identifies specific entities in your text and determines the sentiment associated with each one.
                    </p>
                    <div className="space-y-2">
                      <h4 className="font-medium">Available Providers</h4>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-blue-600 border-blue-200">Google Gemini</Badge>
                          <span className="text-sm text-muted-foreground">Advanced language models with high accuracy</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-orange-600 border-orange-200">Amazon Comprehend</Badge>
                          <span className="text-sm text-muted-foreground">Enterprise-grade natural language processing</span>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-medium">Supported Languages</h4>
                      <div className="flex flex-wrap gap-1">
                        <Badge variant="outline">English</Badge>
                        <Badge variant="outline">Spanish</Badge>
                        <Badge variant="outline">Japanese</Badge>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h3 className="font-semibold">Interpreting Results</h3>
                    <ul className="text-sm text-muted-foreground space-y-2">
                      <li>• <span className="font-medium">Entity Detection</span>: Identifies specific entities (people, places, products) in your text</li>
                      <li>• <span className="font-medium">Sentiment per Entity</span>: Determines positive, negative, or neutral sentiment for each entity</li>
                      <li>• <span className="font-medium">Overall Sentiment</span>: Calculated from the sentiment distribution of all detected entities</li>
                      <li>• <span className="font-medium">Confidence Scores</span>: Shows how certain the AI is about each sentiment classification</li>
                    </ul>
                    <div className="p-4 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg">
                      <p className="text-sm text-green-800 dark:text-green-200">
                        <strong>Advanced AI:</strong> This analyzer uses state-of-the-art language models from Google and Amazon 
                        to provide highly accurate entity-level sentiment analysis with detailed confidence scores.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history" className="space-y-6">
            <Card className="card-enhanced">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  <span>Analysis History</span>
                </CardTitle>
                <CardDescription>
                  Your recent sentiment analyses
                </CardDescription>
              </CardHeader>
              <CardContent>
                {history.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No analysis history yet</p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Your analyzed texts will appear here once you start using the analyzer
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {history.map((item) => (
                      <div key={item.id} className="p-4 border rounded-lg">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              {getSentimentIcon(item.sentiment)}
                              <span className="font-medium capitalize">{item.sentiment}</span>
                              <Badge variant="outline">{formatConfidence(item.confidence)}</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{item.text}</p>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {item.timestamp.toLocaleDateString()} {item.timestamp.toLocaleTimeString()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <Card className="card-enhanced mt-6">
          <CardHeader>
            <CardTitle>Applications</CardTitle>
            <CardDescription>
              How sentiment analysis can be used
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <h3 className="font-semibold">Customer Feedback</h3>
                <p className="text-sm text-muted-foreground">
                  Analyze customer reviews and feedback to understand satisfaction levels and identify areas for improvement.
                </p>
              </div>
              <div className="space-y-2">
                <h3 className="font-semibold">Social Media</h3>
                <p className="text-sm text-muted-foreground">
                  Monitor brand mentions and social media conversations to gauge public opinion about products or services.
                </p>
              </div>
              <div className="space-y-2">
                <h3 className="font-semibold">Market Research</h3>
                <p className="text-sm text-muted-foreground">
                  Analyze survey responses and market research data to understand consumer preferences and trends.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}